// import React, { useRef, useState, useEffect } from "react";
// import { CommentMarker } from "./CommentMarker";

// interface ScreenshotComment {
//   id: number;
//   percentX: number;
//   percentY: number;
//   text: string;
// }

// interface ScreenshotWithCommentsProps {
//   imageUrl: string;
//   comments?: ScreenshotComment[];
//   alt?: string;
//   className?: string;
//   onClick?: () => void;
//   style?: React.CSSProperties;
// }

// export const ScreenshotWithComments: React.FC<ScreenshotWithCommentsProps> = ({
//   imageUrl,
//   comments = [],
//   alt = "Screenshot",
//   className = "",
//   onClick,
//   style,
// }) => {
//   const containerRef = useRef<HTMLDivElement>(null);
//   const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
//   const [activeMarkerId, setActiveMarkerId] = useState<number | null>(null);

//   // Update dimensions when image loads or container resizes
//   useEffect(() => {
//     const updateDimensions = () => {
//       if (containerRef.current) {
//         const rect = containerRef.current.getBoundingClientRect();
//         setDimensions({ width: rect.width, height: rect.height });
//       }
//     };

//     updateDimensions();

//     // Use ResizeObserver to watch for container size changes
//     const resizeObserver = new ResizeObserver(() => {
//       updateDimensions();
//     });

//     if (containerRef.current) {
//       resizeObserver.observe(containerRef.current);
//     }

//     // Also listen to window resize as fallback
//     window.addEventListener("resize", updateDimensions);

//     return () => {
//       resizeObserver.disconnect();
//       window.removeEventListener("resize", updateDimensions);
//     };
//   }, []);

//   const handleImageLoad = () => {
//     if (containerRef.current) {
//       const rect = containerRef.current.getBoundingClientRect();
//       setDimensions({ width: rect.width, height: rect.height });
//     }
//   };

//   const handleMarkerClick = (id: number) => {
//     // Track which marker was clicked (can be used for analytics or other purposes)
//     setActiveMarkerId(id);
//   };

//   return (
//     <div
//       ref={containerRef}
//       style={{
//         position: "relative",
//         width: "100%",
//         display: "inline-block",
//       }}
//     >
//       {/* Screenshot Image */}
//       <img
//         src={imageUrl}
//         alt={alt}
//         onLoad={handleImageLoad}
//         onClick={onClick}
//         className={className}
//         style={style}
//       />

//       {/* Comment Markers Overlay */}
//       {comments && comments.length > 0 && dimensions.width > 0 && (
//         <div
//           style={{
//             position: "absolute",
//             top: 0,
//             left: 0,
//             width: "100%",
//             height: "100%",
//             pointerEvents: "none",
//           }}
//         >
//           {comments.map((comment) => (
//             <div key={comment.id} style={{ pointerEvents: "auto" }}>
//               <CommentMarker
//                 id={comment.id}
//                 percentX={comment.percentX}
//                 percentY={comment.percentY}
//                 text={comment.text}
//                 containerWidth={dimensions.width}
//                 containerHeight={dimensions.height}
//                 onMarkerClick={handleMarkerClick}
//               />
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// };

import React, { useRef, useState, useEffect } from "react";
import { CommentMarker } from "./CommentMarker";

interface ScreenshotComment {
  id: number;
  percentX: number;
  percentY: number;
  text: string;
  username?: string;
  // Optional pixel coordinates (will be converted to percent using image natural dimensions)
  pixelX?: number;
  pixelY?: number;
  images?: string[];
  profilePicture?: string;
}

interface ScreenshotWithCommentsProps {
  imageUrl: string;
  comments?: ScreenshotComment[];
  alt?: string;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
  onImageClick?: (images: string[], startIndex: number) => void;
}

export const ScreenshotWithComments: React.FC<ScreenshotWithCommentsProps> = ({
  imageUrl,
  comments = [],
  alt = "Screenshot",
  className = "",
  onClick,
  style,
  onImageClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [activeMarkerId, setActiveMarkerId] = useState<number | null>(null);

  // Update dimensions when image loads or container resizes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize as fallback
    window.addEventListener("resize", updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // Store natural dimensions for pixel-to-percent conversion
    setNaturalDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    }
  };

  const handleMarkerClick = (id: number) => {
    // Track which marker was clicked (can be used for analytics or other purposes)
    setActiveMarkerId(id);
  };

  const handleImageClick = (e: React.MouseEvent) => {
    // Don't open fullscreen if there's an active marker
    if (activeMarkerId !== null) {
      e.preventDefault();
      e.stopPropagation();
      setActiveMarkerId(null);
      return;
    }
    onClick?.();
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "inline-block",
      }}
    >
      {/* Screenshot Image */}
      <img
        src={imageUrl}
        alt={alt}
        onLoad={handleImageLoad}
        onClick={handleImageClick}
        className={className}
        style={style}
      />

      {/* Comment Markers Overlay */}
      {comments && comments.length > 0 && dimensions.width > 0 && (
        <div
          onClick={(e) => {
            // If there's an active marker, prevent opening fullscreen
            if (activeMarkerId !== null) {
              e.preventDefault();
              e.stopPropagation();
              setActiveMarkerId(null);
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: activeMarkerId !== null ? "auto" : "none",
          }}
        >
          {comments.map((comment) => {
            // If pixelX/pixelY are provided, convert to percentages using natural image dimensions
            let finalPercentX = comment.percentX;
            let finalPercentY = comment.percentY;

            const hasPixelCoords =
              comment.pixelX != null && comment.pixelY != null;

            if (
              hasPixelCoords &&
              naturalDimensions.width > 0 &&
              naturalDimensions.height > 0 &&
              comment.pixelX &&
              comment.pixelY
            ) {
              finalPercentX = (comment.pixelX / naturalDimensions.width) * 100;
              finalPercentY = (comment.pixelY / naturalDimensions.height) * 100;
            } else if (hasPixelCoords) {
              // Don't render yet if we have pixel coords but no natural dimensions
              return null;
            }

            return (
              <div key={comment.id} style={{ pointerEvents: "auto" }}>
                <CommentMarker
                  id={comment.id}
                  percentX={finalPercentX}
                  percentY={finalPercentY}
                  text={comment.text}
                  username={comment.username}
                  containerWidth={dimensions.width}
                  containerHeight={dimensions.height}
                  onMarkerClick={handleMarkerClick}
                  onMarkerUnclick={() => setActiveMarkerId(null)}
                  images={comment.images}
                  onImageClick={onImageClick}
                  profilePicture={comment.profilePicture}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
